import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { WkSemanticSearchService } from '../../services/wk-semantic-search.service';
import {
  SemanticSearchResponse,
  SemanticSearchResult,
} from '../../models/semantic-search.model';

@Component({
  selector: 'wk-semantic-search-results',
  templateUrl: './wk-semantic-search-results.component.html',
  styleUrls: ['./wk-semantic-search-results.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class WkSemanticSearchResultsComponent implements OnInit, OnDestroy {
  public results: SemanticSearchResult[] = [];
  public isLoading = false;
  public searchQuery = '';

  private static readonly MAX_RESULTS = 100;

  private readonly onDestroy$ = new Subject<boolean>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly semanticSearchService: WkSemanticSearchService
  ) {}

  public ngOnInit(): void {
    const parent = this.route.parent;
    if (!parent) {
      return;
    }

    parent.params.pipe(takeUntil(this.onDestroy$)).subscribe((params) => {
      const query = params['q'];
      if (query) {
        this.searchQuery = decodeURIComponent(query);
        this.performSearch();
      }
    });
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }

  public getTitle(result: SemanticSearchResult): string {
    const titleProp = result.propertyValue.find((p) => p.name === 'title');
    return titleProp?.value?.[0] ?? 'Untitled';
  }

  public getSnippet(result: SemanticSearchResult): string {
    return result.payload;
  }

  public openPreview(result: SemanticSearchResult): void {
    const tenantIdProp = result.propertyValue.find(
      (p) => p.name === 'tenantid'
    );
    if (!tenantIdProp || !tenantIdProp.value.length) {
      return;
    }
    const tenantId = tenantIdProp.value[0];
    this.semanticSearchService.getDocumentPreviewUrl(tenantId);
  }

  private performSearch(): void {
    this.isLoading = true;

    this.semanticSearchService
      .search(this.searchQuery, 0, WkSemanticSearchResultsComponent.MAX_RESULTS)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response: SemanticSearchResponse) => {
          const seen = new Set<string>();
          this.results = response.results.filter((result) => {
            const tenantId = result.propertyValue.find(
              (p) => p.name === 'tenantid'
            )?.value?.[0];
            if (!tenantId || seen.has(tenantId)) {
              return false;
            }
            seen.add(tenantId);
            return true;
          });
          this.isLoading = false;
        },
        error: () => {
          this.results = [];
          this.isLoading = false;
        },
      });
  }
}
